import React, { useEffect, useContext, useMemo, useRef, useState } from "react";

import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { Hierarchy, Trash, ArrowDown2, ArrowRight2 } from "iconsax-react";

import useStyles, {
  ActionsDivider,
  ActionsText,
  ActionSwitchContainer,
  AddLogicButton,
  DragQuestionButton,
  LogicQuestionActions,
  LogicQuestionContent,
  LogicQuestionDescription,
  LogicQuestionsWrapper,
  LogicsContainer,
  OptionsErrorSection,
  QuestionContainer,
  RemoveQuestionButton,
} from "./styles";
import { DragIndicator, InfoOutlined } from "@mui/icons-material";
import { Input, Select } from "Components/Form";
import { useTranslation } from "react-i18next";
import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Colors } from "Components/Form/Button";
import { AppSwitch } from "Components/Generic/AppSwitch";
import { OptionsDrawer, OptionsDrawerHandles } from "./OptionsDrawer";
import { QuestionLogics, Questions } from "Models/checklist";
import { OptionsTags } from "./OptionsTags";
import { LogicActions } from "./LogicActions";
import { ChecklistContext, MAXIMUM_LOGICS_PER_QUESTION } from "../../context";
import UITabMaterial, { PanelProps } from "Components/Generic/UITabMaterial";
import {
  findLogicPath,
  findQuestionPath,
  getLogicConditionInitialValue,
  handleQuestionOptions,
  questionTags,
  statementOfAspTags,
  statementOfAspGoShareTags,
  QuestionTypes,
  updateLogicConditions,
  useQuestionErrors,
} from "../../../utils";
import { ChecklistType } from "Util/enums";
import { GO_SHARE_COMPANY_IDS } from "Constants";
import { useAppState } from "Context/StateContext";

interface LogicQuestionsProps {
  isOnViewMode: boolean;
  checklistQuestion: Questions;
  isChild?: boolean;
}

interface DragData {
  questionId?: number;
  questionTemporaryId?: string;
  parentPath: string | number;
}

export const LogicQuestions = ({
  isOnViewMode,
  checklistQuestion,
  isChild = false,
}: LogicQuestionsProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { handleUpdateQuestion, handleRemoveQuestion, handleReorderQuestions, errors, checklist } =
    useContext(ChecklistContext);

  const [question, setQuestion] = useState<Questions>(checklistQuestion);
  const [isFocused, setIsFocused] = useState(false);
  const [isLogicActionsExpanded, setIsLogicActionsExpanded] = useState(!isChild);
  const [currentLogicTab, setCurrentLogicTab] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const [typingTimeout, setTypingTimeout] = useState<any>(0);

  const questionRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);
  const optionsDrawerRef = useRef<OptionsDrawerHandles>(null);

  const classes = useStyles();

  const parentPath = useMemo(() => {
    if (question.logicId) {
      return question.logicId;
    }

    //traverse the checklist to find the parent logic path using temporaryId
    const findParentLogicPath = (questions?: Questions[]): string | number | null => {
      if (!questions) return null;

      const allLogics = questions.flatMap((question) => question.questionLogics ?? []);

      for (const logic of allLogics) {
        const hasMatch = logic.questions?.some(
          (logicQuestion) =>
            (logicQuestion.id && logicQuestion.id === question.id) ||
            (logicQuestion.temporaryId && logicQuestion.temporaryId === question.temporaryId)
        );

        if (hasMatch) {
          return logic.id ?? logic.temporaryId ?? null;
        }

        const nested = findParentLogicPath(logic.questions);

        if (nested) return nested;
      }

      return null;
    };

    const foundPath = findParentLogicPath(checklist?.questions);

    return foundPath || "root";
  }, [question.logicId, question.temporaryId, checklist?.questions]);

  const siblingQuestionsCount = useMemo(() => {
    const countQuestionsAtLevel = (
      questions?: Questions[],
      targetParentPath?: string | number
    ): number => {
      if (!questions?.length) return 0;

      if (targetParentPath === "root") {
        return questions.length;
      }

      const allLogics = questions.flatMap((q) => q.questionLogics ?? []);

      for (const logic of allLogics) {
        const logicPath = logic.id ?? logic.temporaryId;

        if (logicPath === targetParentPath) {
          return logic.questions?.length ?? 0;
        }

        if (logic.questions?.length) {
          const nestedCount = countQuestionsAtLevel(logic.questions, targetParentPath);

          if (nestedCount > 0) return nestedCount;
        }
      }

      return 0;
    };

    return countQuestionsAtLevel(checklist?.questions, parentPath);
  }, [checklist?.questions, parentPath]);

  const questionPath = useMemo(() => {
    return findQuestionPath({
      questions: checklist?.questions,
      questionId: question.id,
      questionTemporaryId: question.temporaryId,
    });
  }, [checklist?.questions, question.id, question.temporaryId]);

  const { titleError, typeError, optionsError } = useQuestionErrors({
    errors,
    questionPath,
  });

  const questionType = useMemo(
    () => [
      { value: QuestionTypes.SINGLE, label: translate("singleChoiceQuestion") },
      { value: QuestionTypes.MULTIPLE, label: translate("multipleChoiceQuestion") },
      { value: QuestionTypes.TEXT, label: translate("inputText") },
      { value: QuestionTypes.NUMBER, label: translate("inputNumber") },
      { value: QuestionTypes.DATE, label: translate("selectDate") },
      { value: QuestionTypes.SIGNATURE, label: translate("addSignature") },
      { value: QuestionTypes.PHOTO, label: translate("addPhoto") },
    ],
    []
  );

  const validQuestionOptions = useMemo(() => {
    return question.options?.filter((option) => !!option.valid) || [];
  }, [question]);

  const tagOptions = useMemo(() => {
    const baseTags = Object.entries(questionTags).flatMap(([_, tags]) =>
      Object.entries(tags).map(([key, label]) => ({ value: key, label }))
    );

    if (checklist?.type === ChecklistType.STATEMENT_OF_ASP) {
      const isGoShareCompany = GO_SHARE_COMPANY_IDS.includes(currentUser?.companyId!);
      const goShareTags = isGoShareCompany ? Object.entries(statementOfAspGoShareTags) : [];

      const aspTags = [...Object.entries(statementOfAspTags), ...goShareTags].map(
        ([key, label]) => ({ value: key, label })
      );

      return [...baseTags, ...aspTags];
    }

    return baseTags;
  }, [checklist?.type, currentUser?.companyId]);

  const isEvidenceQuestion = !!question.isEvidence;

  const canDragQuestion = !isOnViewMode && !isEvidenceQuestion && siblingQuestionsCount >= 2;

  const hasLogic = !!question.questionLogics?.length && !isEvidenceQuestion;

  useEffect(() => {
    const questionElement = questionRef.current;
    const dragHandle = dragHandleRef.current;

    if (!questionElement || !dragHandle || !canDragQuestion) {
      return;
    }

    return combine(
      draggable({
        element: questionElement,
        dragHandle: dragHandle,
        getInitialData: () => ({
          questionId: question.id,
          questionTemporaryId: question.temporaryId,
          parentPath,
        }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: questionElement,
        canDrop: ({ source }) => {
          const data = source.data;
          // only allow dropping in the same parent level
          return (
            data.parentPath === parentPath &&
            (data.questionId !== question.id || data.questionTemporaryId !== question.temporaryId)
          );
        },
        onDragEnter: () => setIsDraggedOver(true),
        onDragLeave: () => setIsDraggedOver(false),
        onDrop: ({ source }) => {
          setIsDraggedOver(false);
          const data = source.data as unknown as DragData;

          if (handleReorderQuestions) {
            handleReorderQuestions({
              sourceQuestionId: data.questionId,
              sourceTemporaryId: data.questionTemporaryId,
              targetQuestionId: question.id,
              targetTemporaryId: question.temporaryId,
              parentPath,
            });
          }
        },
      })
    );
  }, [question.id, question.temporaryId, parentPath, canDragQuestion, handleReorderQuestions]);

  const openOptionsDrawer = (question: Questions, optionsType: string) => {
    optionsDrawerRef.current?.open(question, optionsType);
  };

  const handleUpdateQuestionFields = (attribute: string, value: any) => {
    let questionToUpdate = { ...question, [attribute]: value };

    if (attribute === "type") {
      const logicNewCondition: string = getLogicConditionInitialValue({
        questionType: value,
        isRequired: question.mandatory,
      });

      questionToUpdate = {
        ...questionToUpdate,
        questionLogics: updateLogicConditions({
          newCondition: logicNewCondition,
          logics: question.questionLogics,
          questionType: value,
        }),
        options: handleQuestionOptions(value, question.options),
      };
    }

    if (attribute === "mandatory") {
      const logicNewCondition = getLogicConditionInitialValue({
        questionType: question.type,
        isRequired: value,
      });

      questionToUpdate = {
        ...questionToUpdate,
        questionLogics: updateLogicConditions({
          newCondition: logicNewCondition,
          logics: question.questionLogics,
          questionType: questionToUpdate.type,
        }),
      };
    }

    setQuestion(questionToUpdate);
  };

  useEffect(() => {
    setQuestion(checklistQuestion);
  }, [checklistQuestion]);

  const getQuestionBorderRadius = (): string => {
    if (isChild && !isLogicActionsExpanded) return "0";
    if (isEvidenceQuestion) return "0";
    if (isChild && isLogicActionsExpanded) return "0 0 12px 12px";
    if (hasLogic) return "12px 12px 0 12px";

    return "12px";
  };

  const getContentBorderRadius = (): string => {
    if (isChild) return "0";

    if (!hasLogic) {
      if (isLogicActionsExpanded) {
        return "12px 12px 0 0";
      }

      return "12px";
    }

    if (isLogicActionsExpanded) return "12px 12px 0 0";

    if (hasLogic) return "12px 12px 0 12px";

    return "12px";
  };

  const renderLogics = (questionLogics: QuestionLogics[]) => {
    if (questionLogics.length === 0) return null;

    if (questionLogics.length === 1) {
      return (
        <ul>
          <LogicsContainer isFocused={isFocused}>
            <LogicActions
              questionType={question.type}
              isRequiredQuestion={question.mandatory}
              isOnViewMode={isOnViewMode}
              singleOrMultipleOptions={validQuestionOptions}
              questionLogic={questionLogics[0]}
            />
          </LogicsContainer>
        </ul>
      );
    }

    const getLogicTabErrors = () => {
      return questionLogics.map((logicItem) => {
        const logicPath = findLogicPath({
          questions: checklist?.questions,
          logicId: logicItem.id,
          logicTemporaryId: logicItem.temporaryId,
        });

        if (!logicPath || !errors) return false;

        return errors.some((error) => error.path?.startsWith(logicPath));
      });
    };

    const tabErrors = getLogicTabErrors();

    const leftPanels: PanelProps[] =
      questionLogics?.map((logicItem, index) => ({
        menuItem: `${translate("option")} ${index + 1}`,
        key: `option-${index}`,
        className: classes.tabPanel,
        hasError: tabErrors[index],
        render: () => (
          <LogicActions
            questionType={question.type}
            isRequiredQuestion={question.mandatory}
            isOnViewMode={isOnViewMode}
            singleOrMultipleOptions={validQuestionOptions}
            questionLogic={logicItem}
          />
        ),
      })) || [];

    return (
      <ul>
        <LogicsContainer isFocused={isFocused}>
          <div className={classes.leftPanelContainer}>
            <UITabMaterial
              className={classes.tabsContainer}
              leftPanels={leftPanels}
              leftPanelClassName={classes.leftPanel}
              selectedLeftPanel={currentLogicTab}
              onChangeLeftPanel={(_, value) => {
                setCurrentLogicTab(Number(value));
              }}
            />
          </div>
        </LogicsContainer>
      </ul>
    );
  };

  return (
    <QuestionContainer
      ref={questionRef}
      key={question.id || question.temporaryId}
      tabIndex={0}
      isDragging={isDragging}
      onClick={(event) => {
        event.stopPropagation();
        setIsFocused(true);
        setIsLogicActionsExpanded(true);
      }}
      onBlur={(event) => {
        event.stopPropagation();
        setIsFocused(false);
      }}
      onFocus={(event) => {
        event.stopPropagation();
        setIsFocused(true);
      }}
    >
      <LogicQuestionsWrapper
        isFocused={isFocused}
        isDraggedOver={isDraggedOver}
        borderRadius={getQuestionBorderRadius()}
      >
        <LogicQuestionContent borderRadius={getContentBorderRadius()}>
          {hasLogic ? (
            <DragQuestionButton
              ref={dragHandleRef}
              canDragQuestion={canDragQuestion}
              onClick={(event) => {
                event.stopPropagation();
                setIsLogicActionsExpanded((isLogicActionsExpanded) => !isLogicActionsExpanded);
              }}
            >
              {isLogicActionsExpanded ? (
                <ArrowDown2 fontSize="large" />
              ) : (
                <ArrowRight2 fontSize="large" />
              )}
            </DragQuestionButton>
          ) : (
            <DragQuestionButton ref={dragHandleRef} canDragQuestion={canDragQuestion}>
              <DragIndicator fontSize="large" />
            </DragQuestionButton>
          )}

          <Input
            disabled={isOnViewMode || isEvidenceQuestion}
            name="title"
            placeholder={translate("enterQuestionHere")}
            error={!!titleError}
            errorMessage={titleError}
            value={question.title}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (typingTimeout) clearTimeout(typingTimeout);

              handleUpdateQuestionFields("title", event.target.value);
              setTypingTimeout(
                setTimeout(
                  () =>
                    handleUpdateQuestion({
                      value: event.target.value,
                      field: "title",
                      temporaryId: question.temporaryId,
                      questionId: question.id,
                    }),
                  500
                )
              );
            }}
          />

          <Select
            data-cy="tagSelect"
            disabled={isOnViewMode || isEvidenceQuestion}
            name="tag"
            placeholder={translate("tag")}
            value={question.tag}
            onChange={(event: SelectChangeEvent<any>) => {
              if (typingTimeout) clearTimeout(typingTimeout);

              handleUpdateQuestionFields("tag", event.target.value);
              setTypingTimeout(
                setTimeout(
                  () =>
                    handleUpdateQuestion({
                      value: event.target.value,
                      field: "tag",
                      temporaryId: question.temporaryId,
                      questionId: question.id,
                    }),
                  500
                )
              );
            }}
          >
            {tagOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>

          <Select
            data-cy="typeSelect"
            disabled={isOnViewMode || isEvidenceQuestion}
            name="type"
            placeholder={translate("responseType")}
            error={!!typeError}
            errorMessage={typeError}
            value={question.type}
            onChange={(event: SelectChangeEvent<any>) => {
              if (typingTimeout) clearTimeout(typingTimeout);

              handleUpdateQuestionFields("type", event.target.value);
              setTypingTimeout(
                setTimeout(
                  () =>
                    handleUpdateQuestion({
                      value: event.target.value,
                      field: "type",
                      temporaryId: question.temporaryId,
                      questionId: question.id,
                    }),
                  500
                )
              );
            }}
          >
            {questionType.map((type) => (
              <MenuItem
                data-cy={type.label.toLowerCase().replace(" ", "")}
                value={type.value}
                key={type.value}
                onClick={
                  type.value === QuestionTypes.SINGLE || type.value === QuestionTypes.MULTIPLE
                    ? () => openOptionsDrawer(question, type.value)
                    : undefined
                }
              >
                {type.label}
              </MenuItem>
            ))}
          </Select>

          <RemoveQuestionButton
            onClick={() =>
              handleRemoveQuestion({
                temporaryId: question.temporaryId,
                questionId: question.id,
              })
            }
            disabled={isOnViewMode || isEvidenceQuestion}
          >
            <Trash />
          </RemoveQuestionButton>
        </LogicQuestionContent>

        {question?.hasDescription && isLogicActionsExpanded && !isEvidenceQuestion && (
          <LogicQuestionDescription>
            <div />
            <Input
              disabled={isOnViewMode}
              name="description"
              placeholder={translate("enterDescriptionHere")}
              value={question.description}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (typingTimeout) clearTimeout(typingTimeout);

                handleUpdateQuestionFields("description", event.target.value);

                setTypingTimeout(
                  setTimeout(
                    () =>
                      handleUpdateQuestion({
                        value: event.target.value,
                        field: "description",
                        temporaryId: question.temporaryId,
                        questionId: question.id,
                      }),
                    500
                  )
                );
              }}
            />
          </LogicQuestionDescription>
        )}

        {isLogicActionsExpanded && !isEvidenceQuestion && (
          <LogicQuestionActions hasLogic={hasLogic}>
            {question.type !== QuestionTypes.DATE &&
              question.type !== QuestionTypes.SIGNATURE &&
              question.type !== QuestionTypes.PHOTO && (
                <>
                  <AddLogicButton
                    customColor={Colors.SECONDARY_DARK_OUTLINED}
                    startIcon={<Hierarchy size={16} />}
                    disabled={
                      (question.questionLogics &&
                        question.questionLogics?.length >= MAXIMUM_LOGICS_PER_QUESTION) ||
                      isOnViewMode
                    }
                    onClick={() => {
                      const questionTemporaryId = Math.random().toString(36).substring(2, 15);
                      const questionCondition = getLogicConditionInitialValue({
                        questionType: question.type,
                        isRequired: question.mandatory,
                      });

                      handleUpdateQuestionFields("questionLogics", [
                        ...(question.questionLogics ? question.questionLogics : []),
                        {
                          logicCondition: questionCondition,
                          logicValue: "",
                          isEvidenceNotesRequired: false,
                          isEvidencePhotosRequired: false,
                          temporaryId: questionTemporaryId,
                          ...(question.id ? { questionId: question.id } : {}),
                          questions: [],
                        },
                      ]);

                      handleUpdateQuestion({
                        value: [
                          ...(question.questionLogics ? question.questionLogics : []),
                          {
                            logicCondition: questionCondition,
                            logicValue: "",
                            isEvidenceNotesRequired: false,
                            isEvidencePhotosRequired: false,
                            temporaryId: questionTemporaryId,
                            ...(question.id ? { questionId: question.id } : {}),
                            questions: [],
                          },
                        ],
                        field: "questionLogics",
                        temporaryId: question.temporaryId,
                        questionId: question.id,
                      });
                    }}
                  >
                    {translate("addLogic")}
                  </AddLogicButton>
                  <ActionsDivider />
                </>
              )}
            <ActionSwitchContainer>
              <AppSwitch
                disabled={isOnViewMode}
                checked={question.hasDescription}
                onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  handleUpdateQuestionFields("hasDescription", checked);

                  handleUpdateQuestion({
                    value: checked,
                    field: "hasDescription",
                    temporaryId: question.temporaryId,
                    questionId: question.id,
                  });
                }}
              />
              <ActionsText>{translate("shortDescription")}</ActionsText>
            </ActionSwitchContainer>
            <ActionsDivider />
            <ActionSwitchContainer>
              <AppSwitch
                disabled={isOnViewMode}
                checked={question.mandatory}
                onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  handleUpdateQuestionFields("mandatory", checked);

                  handleUpdateQuestion({
                    value: checked,
                    field: "mandatory",
                    temporaryId: question.temporaryId,
                    questionId: question.id,
                  });
                }}
              />
              <ActionsText>{translate("required")}</ActionsText>
            </ActionSwitchContainer>

            {!!validQuestionOptions?.length && (
              <OptionsTags
                tags={validQuestionOptions}
                handleOpenDrawer={() => openOptionsDrawer(question, question.type)}
                isOnViewMode={isOnViewMode}
              />
            )}
            {!!optionsError && (
              <OptionsErrorSection>
                <InfoOutlined className={classes.iconError} />
                {optionsError}
              </OptionsErrorSection>
            )}
          </LogicQuestionActions>
        )}
      </LogicQuestionsWrapper>
      {hasLogic &&
        isLogicActionsExpanded &&
        question.questionLogics &&
        renderLogics(question.questionLogics)}

      <OptionsDrawer ref={optionsDrawerRef} />
    </QuestionContainer>
  );
};
